import { CSSProperties } from 'react';
import { ColorTheme } from '../colors';

export const getBlogStyles = (theme: ColorTheme): { [key: string]: CSSProperties } => ({

  // ── List page ────────────────────────────────────────────────────────────
  pageWrapper: {
    minHeight: '100vh',
    background: theme.backgroundGradient,
    paddingTop: '80px',
  },
  container: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '3rem 1.5rem',
  },
  pageHeader: {
    marginBottom: '2.5rem',
  },
  pageTitle: {
    fontSize: '2.5rem',
    fontWeight: 800,
    background: theme.textGradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '0.5rem',
  },
  pageSubtitle: {
    color: theme.textSecondary,
    fontSize: '1.05rem',
  },

  // ── Tag filter bar ────────────────────────────────────────────────────────
  tagBar: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const,
    marginBottom: '2rem',
  },
  tagBtn: {
    padding: '0.35rem 1rem',
    borderRadius: '999px',
    border: `1px solid ${theme.cardBorder}`,
    background: theme.cardBackground,
    color: theme.textSecondary,
    cursor: 'pointer',
    fontSize: '0.85rem',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  },
  tagBtnActive: {
    background: theme.primaryGradient,
    color: theme.textWhite,
    border: '1px solid transparent',
    fontWeight: 600,
  },

  // ── Blog grid ─────────────────────────────────────────────────────────────
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '1.75rem',
  },

  // ── Blog card ─────────────────────────────────────────────────────────────
  card: {
    background: theme.cardBackground,
    borderRadius: '16px',
    border: `1px solid ${theme.cardBorder}`,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  cardCover: {
    width: '100%',
    height: '200px',
    objectFit: 'cover' as const,
    display: 'block',
    background: theme.surfaceBackground,
  },
  cardBody: {
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
  },
  cardTags: {
    display: 'flex',
    gap: '0.4rem',
    flexWrap: 'wrap' as const,
    marginBottom: '0.6rem',
  },
  cardTag: {
    fontSize: '0.68rem',
    padding: '0.2rem 0.6rem',
    borderRadius: '999px',
    background: theme.badgeBackground,
    color: theme.badgeText,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  cardTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: theme.textPrimary,
    marginBottom: '0.5rem',
    lineHeight: 1.4,
  },
  cardSummary: {
    fontSize: '0.88rem',
    color: theme.textSecondary,
    lineHeight: 1.65,
    flex: 1,
    marginBottom: '1rem',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as const,
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardMeta: {
    fontSize: '0.78rem',
    color: theme.textLight,
  },
  readMoreBtn: {
    background: theme.primaryGradient,
    color: theme.textWhite,
    border: 'none',
    borderRadius: '8px',
    padding: '0.4rem 1rem',
    fontSize: '0.82rem',
    cursor: 'pointer',
    fontWeight: 600,
    fontFamily: 'inherit',
  },

  // ── Empty state ───────────────────────────────────────────────────────────
  emptyState: {
    textAlign: 'center' as const,
    padding: '5rem 2rem',
    color: theme.textSecondary,
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  emptyText: {
    fontSize: '1rem',
    color: theme.textSecondary,
  },

  // ── Pagination ────────────────────────────────────────────────────────────
  pagination: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'center',
    marginTop: '3rem',
    flexWrap: 'wrap' as const,
  },
  pageBtn: {
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    border: `1px solid ${theme.cardBorder}`,
    background: theme.cardBackground,
    color: theme.textSecondary,
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
  },
  pageBtnActive: {
    background: theme.primaryGradient,
    color: theme.textWhite,
    border: '1px solid transparent',
    fontWeight: 600,
  },

  // ── Detail page ───────────────────────────────────────────────────────────
  detailWrapper: {
    minHeight: '100vh',
    background: theme.backgroundGradient,
    paddingTop: '80px',
  },
  detailContainer: {
    maxWidth: '780px',
    margin: '0 auto',
    padding: '2rem 1.5rem 5rem',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    color: theme.textSecondary,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.9rem',
    marginBottom: '1.75rem',
    padding: 0,
    fontFamily: 'inherit',
  },
  heroCover: {
    width: '100%',
    maxHeight: '440px',
    objectFit: 'cover' as const,
    borderRadius: '16px',
    marginBottom: '2rem',
    display: 'block',
  },
  detailTitle: {
    fontSize: '2rem',
    fontWeight: 800,
    color: theme.textPrimary,
    lineHeight: 1.3,
    marginBottom: '1rem',
  },
  detailMeta: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.75rem 1.25rem',
    alignItems: 'center',
    fontSize: '0.85rem',
    color: theme.textSecondary,
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: `1px solid ${theme.cardBorder}`,
  },
  detailTag: {
    fontSize: '0.68rem',
    padding: '0.2rem 0.6rem',
    borderRadius: '999px',
    background: theme.badgeBackground,
    color: theme.badgeText,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },

  // ── Block styles ──────────────────────────────────────────────────────────
  blockContent: {
    lineHeight: 1.8,
  },
  blockHeading1: {
    fontSize: '1.75rem',
    fontWeight: 800,
    color: theme.textPrimary,
    margin: '2.5rem 0 1rem',
    lineHeight: 1.3,
  },
  blockHeading2: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: theme.textPrimary,
    margin: '2rem 0 0.75rem',
    lineHeight: 1.3,
  },
  blockHeading3: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.textPrimary,
    margin: '1.5rem 0 0.5rem',
  },
  blockParagraph: {
    fontSize: '1rem',
    color: theme.textSecondary,
    lineHeight: 1.85,
    margin: '0 0 1.25rem',
  },
  blockImageWrap: {
    margin: '1.75rem 0',
  },
  blockImageImg: {
    width: '100%',
    borderRadius: '12px',
    display: 'block',
  },
  blockImageCaption: {
    fontSize: '0.8rem',
    color: theme.textLight,
    textAlign: 'center' as const,
    marginTop: '0.5rem',
    fontStyle: 'italic',
  },
  blockImageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '0.75rem',
    margin: '1.75rem 0',
  },
  blockImageGridItem: {
    borderRadius: '10px',
    overflow: 'hidden',
  },
  blockImageGridImg: {
    width: '100%',
    height: '160px',
    objectFit: 'cover' as const,
    display: 'block',
  },
  blockListUl: {
    margin: '0 0 1.25rem 1.5rem',
    color: theme.textSecondary,
    lineHeight: 2,
    fontSize: '1rem',
  },
  blockListOl: {
    margin: '0 0 1.25rem 1.5rem',
    color: theme.textSecondary,
    lineHeight: 2,
    fontSize: '1rem',
  },
  blockQuote: {
    borderLeft: `4px solid ${theme.primary}`,
    paddingLeft: '1.25rem',
    margin: '1.75rem 0',
    color: theme.textSecondary,
    fontStyle: 'italic',
    fontSize: '1.05rem',
    lineHeight: 1.7,
  },
  blockQuoteAuthor: {
    fontSize: '0.82rem',
    color: theme.textLight,
    fontStyle: 'normal',
    marginTop: '0.5rem',
    display: 'block',
  },
  blockCallout: {
    background: theme.surfaceBackground,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '12px',
    padding: '1rem 1.25rem',
    margin: '1.5rem 0',
    fontSize: '0.95rem',
    color: theme.textSecondary,
    lineHeight: 1.7,
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-start',
  },
  blockCalloutIcon: {
    fontSize: '1.1rem',
    flexShrink: 0,
    marginTop: '0.1rem',
  },
  blockDivider: {
    border: 'none',
    borderTop: `1px solid ${theme.cardBorder}`,
    margin: '2.25rem 0',
  },
  blockVideoWrap: {
    margin: '1.75rem 0',
    borderRadius: '12px',
    overflow: 'hidden',
    background: theme.surfaceBackground,
    aspectRatio: '16/9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  blockVideoIframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
  },
  blockStatHighlight: {
    display: 'flex',
    gap: '1.5rem',
    flexWrap: 'wrap' as const,
    margin: '1.75rem 0',
    background: theme.surfaceBackground,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '16px',
    padding: '1.5rem',
  },
  blockStat: {
    textAlign: 'center' as const,
    flex: '1 1 80px',
  },
  blockStatValue: {
    fontSize: '1.6rem',
    fontWeight: 800,
    background: theme.textGradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    display: 'block',
  },
  blockStatLabel: {
    fontSize: '0.78rem',
    color: theme.textLight,
    marginTop: '0.25rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  blockRecipeCard: {
    background: theme.surfaceBackground,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '16px',
    padding: '1.5rem',
    margin: '1.75rem 0',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
  },
  blockRecipeTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: theme.badgeText,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    marginBottom: '0.75rem',
  },
  blockRecipeList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    color: theme.textSecondary,
    fontSize: '0.9rem',
    lineHeight: 1.9,
  },

  // ── Related posts ─────────────────────────────────────────────────────────
  relatedSection: {
    marginTop: '3.5rem',
    paddingTop: '2rem',
    borderTop: `1px solid ${theme.cardBorder}`,
  },
  relatedTitle: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: theme.textPrimary,
    marginBottom: '1.25rem',
  },
  relatedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: '1rem',
  },
  relatedCard: {
    background: theme.cardBackground,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  relatedCardImg: {
    width: '100%',
    height: '130px',
    objectFit: 'cover' as const,
    display: 'block',
    background: theme.surfaceBackground,
  },
  relatedCardBody: {
    padding: '0.75rem',
  },
  relatedCardTitle: {
    fontSize: '0.88rem',
    fontWeight: 600,
    color: theme.textPrimary,
    lineHeight: 1.4,
  },
  relatedCardMeta: {
    fontSize: '0.75rem',
    color: theme.textLight,
    marginTop: '0.3rem',
  },

  // ── Loading / error ───────────────────────────────────────────────────────
  loading: {
    textAlign: 'center' as const,
    padding: '5rem 2rem',
    color: theme.textSecondary,
    fontSize: '1rem',
  },
  errorBox: {
    textAlign: 'center' as const,
    padding: '4rem 2rem',
    color: theme.textSecondary,
  },
});
